import React from 'react';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from 'primereact/button';

import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type SmartFeeFormValues = {
  apiKey: string;
  password: string;
  userId: string;
  urlOverride?: string;
};

interface EditSmartFeeIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditSmartFeeFeeIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditSmartFeeIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let apiKey = '';
  let password = '';
  let userId = '';
  let urlOverride = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    apiKey = data.apiKey || '';
    password = data.password || '';
    userId = data.userId || '';
    urlOverride = data.urlOverride || '';
  }

  const validationSchema = yup.object().shape({
    apiKey: yup.string().required('API Key is required'),
    password: yup.string().required('Password is required'),
    userId: yup.string().required('User ID is required'),
    urlOverride: yup.string(),
  });

  const { control, handleSubmit, formState } = useForm<SmartFeeFormValues>({
    defaultValues: {
      apiKey: apiKey,
      password: password,
      userId: userId,
      urlOverride: urlOverride,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: SmartFeeFormValues) => {
    console.log('Form validated successfully:', data);

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          vendor: IntegrationVendor.SmartFees,
          environment: environment,
          integrationData: JSON.stringify({
            apiKey: data.apiKey,
            password: data.password,
            userId: data.userId,
            urlOverride: data.urlOverride || undefined,
          }),
        };
      } else {
        settings = {
          name: 'Smart Fee Integration',
          vendor: IntegrationVendor.SmartFees,
          environment: environment,
          status: IntegrationStatus.Active,
          integrationData: JSON.stringify({
            apiKey: data.apiKey,
            password: data.password,
            userId: data.userId,
            urlOverride: data.urlOverride || undefined,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit SmartFee Fee Settings' : 'SmartFee Fee Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="apiKey"
            label="API Key*"
            placeholder="Enter your Smart Fee API Key"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="password"
            label="Password*"
            placeholder="Enter your Smart Fee Password"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="userId"
            label="User ID*"
            placeholder="Enter your Smart Fee User ID"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="urlOverride"
            label="URL Override"
            placeholder="Enter URL override (optional)"
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
